import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import { apiUrl, basePath } from '../../api/envUrl';
import { exportToExcel } from '../../utils/exportExcel';
import { useSnackbar } from '../../utils/snackbar';

interface ImportResult {
  brandsCreated: number;
  modelsCreated: number;
  variantsCreated: number;
  phonesAdded: number;
  /** Pieces of counted stock - covers, chargers - which carry no serial number. */
  unitsAdded?: number;
  skippedCount: number;
  skipped: string[];
}

/** The columns the importer reads, and what each one is for. */
const COLUMNS: { name: string; required?: boolean; meaning: string; example: string }[] = [
  {
    name: 'Product Name',
    meaning: "The shop's own name for it. Left blank, one is built from brand, model and variant",
    example: 'Samsung ON8 Green 8 GB / 128 GB',
  },
  { name: 'Brand', required: true, meaning: 'Created if the shop does not have it yet', example: 'Samsung' },
  { name: 'Model', required: true, meaning: 'Created under that brand', example: 'ON8' },
  { name: 'Colour', meaning: 'Part of the product name if you leave the name out', example: 'Green' },
  { name: 'RAM', meaning: 'As written on the box', example: '8 GB' },
  { name: 'Storage', meaning: 'As written on the box', example: '128 GB' },
  { name: 'MRP', meaning: 'Printed price', example: '19999' },
  { name: 'Selling Price', meaning: 'What the shop actually charges', example: '18999' },
  { name: 'HSN', meaning: 'Tax code for the bill', example: '8517' },
  { name: 'GST', meaning: 'Rate as a number, no % sign', example: '18' },
  {
    name: 'Serial Number',
    meaning: 'For handsets: the IMEI or serial number, one row per piece. Leave blank for anything counted',
    example: '860011122233301',
  },
  {
    name: 'Quantity',
    meaning: 'For covers, chargers and anything counted: how many arrived. Leave blank for handsets',
    example: '25',
  },
  { name: 'Cost', meaning: 'What the shop paid for one piece', example: '15000' },
];

/**
 * Day-one load of the shop's existing stock from a spreadsheet.
 *
 * <p>The shop is given the template rather than asked to match a format from a
 * description - a sheet with the right headings and one filled row is far harder to
 * get wrong than a paragraph explaining the same thing.
 *
 * <p>Sent as a plain upload rather than through the API layer, because that layer is
 * built around JSON.
 */
const ImportStock = () => {
  const { openSnackbar } = useSnackbar();
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  /** A ready-made sheet: the right headings, and two rows showing the shape. */
  const downloadTemplate = () => {
    const row = (values: Record<string, string>) => {
      const built: Record<string, string> = {};
      COLUMNS.forEach((column) => {
        built[column.name] = values[column.name] ?? '';
      });
      return built;
    };

    /*
     * Three sample rows, because there are two kinds of stock and the sheet has to
     * show both: a handset fills Serial Number and leaves Quantity empty, an accessory
     * does the opposite. Filling every column on every row - which is what this used to
     * do - made it look as though a handset needed a quantity too.
     */
    const handset = (imei: string) =>
      row({
        'Product Name': 'Samsung ON8 Green 8 GB / 128 GB',
        Brand: 'Samsung',
        Model: 'ON8',
        Colour: 'Green',
        RAM: '8 GB',
        Storage: '128 GB',
        MRP: '19999',
        'Selling Price': '18999',
        HSN: '8517',
        GST: '18',
        'Serial Number': imei,
        Cost: '15000',
      });

    const accessory = row({
      'Product Name': 'Back Cover - Samsung ON8',
      Brand: 'Samsung',
      Model: 'ON8',
      MRP: '499',
      'Selling Price': '399',
      HSN: '3926',
      GST: '18',
      Quantity: '25',
      Cost: '150',
    });

    exportToExcel([handset('860011122233301'), handset('860011122233302'), accessory], 'stock-import-template', 'Stock');
    openSnackbar('Template downloaded. The first two rows are handsets, the third is counted stock.', 'success');
  };

  const upload = async (file: File) => {
    setBusy(true);
    setResult(null);
    try {
      const body = new FormData();
      body.append('file', file);
      const token = localStorage.getItem('mobileShopToken');
      const response = await fetch(`${apiUrl}${basePath}/system/import-stock`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.message || 'Import failed');
      setResult(data as ImportResult);

      /*
       * A file where every row was skipped - all the serial numbers already in stock,
       * say - used to be announced in green as "0 phone(s) added to stock". Read at a
       * glance that is a success message, so the shop walks away believing the import
       * worked while nothing has changed. It is the likeliest reason for a report that
       * importing "does not work" when the file was in fact read correctly.
       *
       * The counts pick the wording now; the skipped rows underneath say why.
       */
      const added = (Number(data.phonesAdded) || 0) + (Number(data.unitsAdded) || 0);
      const skipped = Number(data.skippedCount) || 0;
      if (added === 0) {
        openSnackbar(
          skipped > 0
            ? `Nothing was added - all ${skipped} row(s) were skipped. The reasons are below.`
            : 'Nothing was added. The sheet had no stock rows to read.',
          'warning',
        );
      } else {
        openSnackbar(
          skipped > 0
            ? `${added} piece(s) added to stock, ${skipped} row(s) skipped. The reasons are below.`
            : `${added} piece(s) added to stock`,
          skipped > 0 ? 'info' : 'success',
        );
      }
    } catch (error: any) {
      openSnackbar(error?.message || 'Could not import the file', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={700}>
        Import stock from Excel
      </Typography>
      <Typography variant="caption" color="text.secondary">
        For the first day, when the shop already has stock on the shelf. Brands, models and products are created as they are met.
      </Typography>

      {/* ------------------------------------------------- how to use it */}
      <Card sx={{ p: 3, mt: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} color="primary.main" sx={{ mb: 1.5 }}>
          HOW TO USE IT
        </Typography>

        <Stack gap={1.5}>
          {[
            'Press "Download Template" - it opens as an Excel file with the right column headings and three sample rows.',
            'Delete the sample rows and type your own stock. A handset needs one row per piece with its serial number; covers and chargers need one row with a quantity.',
            'A row with neither a Serial Number nor a Quantity creates the product and adds no stock, which is a quick way to load the product list on its own.',
            'Save the file, then press "Choose Excel File" below and pick it.',
            'Check the result. Any row that could not be loaded is listed with the reason, and nothing else is affected.',
          ].map((step, index) => (
            <Stack key={step} direction="row" gap={1.5} alignItems="flex-start">
              <Chip size="small" label={index + 1} color="primary" sx={{ minWidth: 28 }} />
              <Typography variant="body2">{step}</Typography>
            </Stack>
          ))}
        </Stack>

        <Alert severity="info" sx={{ mt: 2 }}>
          The file is safe to run twice. A serial number already in stock is skipped and reported rather than added again.
        </Alert>

        <Stack direction="row" gap={2} sx={{ mt: 2 }} flexWrap="wrap">
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadTemplate}>
            Download Template
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) {
                setFileName(file.name);
                upload(file);
              }
              event.target.value = '';
            }}
          />
          <LoadingButton variant="contained" startIcon={<UploadFileIcon />} loading={busy} onClick={() => inputRef.current?.click()}>
            Choose Excel File
          </LoadingButton>
          {fileName && (
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
              {fileName}
            </Typography>
          )}
        </Stack>

        {result && (
          <Box sx={{ mt: 3 }}>
            {/*
              Green only when something actually arrived in stock.

              Judged on skippedCount alone, a sheet that added nothing and skipped
              nothing - a file of product rows with no serial number and no quantity -
              came out as a green "0 phone(s) added", which reads as success. The
              snackbar already says otherwise, so the screen contradicted itself.
            */}
            <Alert
              severity={
                (Number(result.phonesAdded) || 0) + (Number(result.unitsAdded) || 0) > 0 && !result.skippedCount
                  ? 'success'
                  : 'warning'
              }
            >
              {result.phonesAdded} phone(s) added
              {Number(result.unitsAdded) > 0 ? ` · ${result.unitsAdded} unit(s) of counted stock added` : ''} &middot;{' '}
              {result.brandsCreated} brand(s), {result.modelsCreated} model(s), {result.variantsCreated} product(s) created
              {result.skippedCount ? ` · ${result.skippedCount} row(s) skipped` : ''}
            </Alert>
            {result.skipped?.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  SKIPPED ROWS
                </Typography>
                {result.skipped.map((line) => (
                  <Typography key={line} variant="body2" sx={{ fontSize: '.8rem' }}>
                    {line}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Card>

      {/* --------------------------------------------------- the columns */}
      <Card sx={{ p: 3, mt: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} color="primary.main">
          THE COLUMNS
        </Typography>
        <Typography variant="caption" color="text.secondary">
          The first row of the sheet must name them. Order does not matter, and a column you do not need can be left out.
        </Typography>
        <Divider sx={{ my: 1.5 }} />

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Column</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>What it is</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Example</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {COLUMNS.map((column) => (
              <TableRow key={column.name} hover>
                <TableCell>
                  <Stack direction="row" gap={1} alignItems="center">
                    <Typography variant="body2" fontWeight={600}>
                      {column.name}
                    </Typography>
                    {column.required && <Chip size="small" color="primary" variant="outlined" label="Required" />}
                  </Stack>
                </TableCell>
                <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>{column.meaning}</TableCell>
                <TableCell sx={{ fontSize: 13 }}>{column.example}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </Box>
  );
};

export default ImportStock;
